import { useMemo, useState } from "react";

import { Colors } from "@blueprintjs/core";
import {
  Column,
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { Link, useParams } from "react-router";

import NoParticipantsSvg from "@/../../hypetrain/static/img/program/no-participants.svg";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { Box, Flex } from "@/components/layout/flexbox";
import { getProgramComponentPath } from "@/components/pages_logged_in/program/components/get-program-component-path";
import { roleplayRankStyle } from "@/components/pages_logged_in/program/components/program-details/const";
import { RankIcon } from "@/components/pieces/roleplays/RankIcon";
import { Avatar } from "@/components/pieces/users/Avatar";
import { SUBTEXT_COLOR } from "@/css/constants";
import {
  ComponentTypeEnum,
  ProgramRoleplayScenarioParticipantsQuery,
  RoleplayRank,
  useProgramRoleplayScenarioParticipantsQuery,
} from "@/graphql";
import { NonNullableArrayItem } from "@/types/utils";
import { ensureNonNullArray } from "@/utils/array";

interface ScenarioParticipantProps {
  componentId: string;
  scenarioId: string;
}

type Participant = NonNullableArrayItem<
  ProgramRoleplayScenarioParticipantsQuery["programRoleplayScenarioParticipants"]
> & {
  numberOfSession?: number;
  bestRank?: RoleplayRank;
  responseId?: string | null;
};

type Session = NonNullableArrayItem<
  NonNullableArrayItem<
    ProgramRoleplayScenarioParticipantsQuery["programRoleplayScenarioParticipants"]
  >["roleplaySessions"]
>;

const getBestRank = (roleplaySessions: Session[]) => {
  if (!roleplaySessions) return RoleplayRank.Unranked;

  const { bestRank } = roleplaySessions.reduce<{
    bestScore: number;
    bestRank: RoleplayRank;
  }>(
    (best, session) => {
      const score = session.score ?? 0;
      if (score > best.bestScore) {
        return {
          bestScore: score,
          bestRank: session.rank ?? RoleplayRank.Unranked,
        };
      }
      return best;
    },
    {
      bestScore: 0,
      bestRank: RoleplayRank.Unranked,
    },
  );
  return bestRank;
};

const sanitizeParticipants = (participants: Participant[]): Participant[] => {
  return participants.map((participant) => {
    const {
      roleplaySessions,
      id,
      name,
      photo,
      email,
      programComponentResponseId,
    } = participant;

    const sessions = ensureNonNullArray(roleplaySessions);

    return {
      id,
      name,
      email,
      photo,
      numberOfSession: sessions.length,
      bestRank: getBestRank(sessions),
      responseId: programComponentResponseId,
    };
  });
};

export function ScenarioParticipant({
  componentId,
  scenarioId,
}: ScenarioParticipantProps) {
  const { id: programId } = useParams();
  const { data } = useProgramRoleplayScenarioParticipantsQuery({
    fetchPolicy: "no-cache",
    variables: { programComponentId: componentId, scenarioId },
  });

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "user__name",
      desc: false,
    },
  ]);

  const participants = useMemo(
    () =>
      sanitizeParticipants(
        ensureNonNullArray(data?.programRoleplayScenarioParticipants),
      ),
    [data],
  );

  const columns: ColumnDef<Participant>[] = [
    {
      id: "user__name",
      header: ({ column }: { column: Column<Participant, unknown> }) => (
        <DataTableColumnHeader column={column} title="Name" />
      ),
      accessorKey: "name",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const nameA =
          rowA.original.name?.toLowerCase() ||
          rowA.original.email?.toLowerCase() ||
          "";
        const nameB =
          rowB.original.name?.toLowerCase() ||
          rowB.original.email?.toLowerCase() ||
          "";
        return nameA.localeCompare(nameB);
      },
      cell: ({ row }: { row: Row<Participant> }) => {
        const { name, photo, email, responseId } = row.original;
        const userText = name || email || "";
        const path = getProgramComponentPath({
          type: ComponentTypeEnum.AiRoleplay,
          programId,
          componentId,
          responseId,
        });
        return (
          <Flex alignItems={"center"}>
            <Box mr={2}>
              <Avatar size={20} user={{ name, photo }} />
            </Box>
            {responseId ? (
              <Link to={path}>{userText}</Link>
            ) : (
              <Box color={SUBTEXT_COLOR}>{userText}</Box>
            )}
          </Flex>
        );
      },
    },
    {
      id: "numberOfSession",
      header: ({ column }: { column: Column<Participant, unknown> }) => (
        <DataTableColumnHeader column={column} title="Num Sessions" />
      ),
      accessorKey: "numberOfSession",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.numberOfSession || 0;
        const b = rowB.original.numberOfSession || 0;
        return a - b;
      },
      cell: ({ row }: { row: Row<Participant> }) => {
        const numSessions = row.original.numberOfSession || 0;
        return (
          <Box fontSize={14} fontWeight={500} ml="5px" color={SUBTEXT_COLOR}>
            {numSessions > 0
              ? `${numSessions} ${numSessions > 1 ? "sessions" : "session"}`
              : "-"}
          </Box>
        );
      },
    },
    {
      id: "bestRank",
      header: ({ column }: { column: Column<Participant, unknown> }) => (
        <DataTableColumnHeader column={column} title="Best Performance" />
      ),
      accessorKey: "bestRank",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        // Higher rankValue means higher rank (Gold=3, Silver=2, Bronze=1, Unranked=0)

        const getRankValue = (rank: RoleplayRank) => {
          return roleplayRankStyle[rank]?.rankValue || 0;
        };
        return (
          getRankValue(rowA.original.bestRank || RoleplayRank.Unranked) -
          getRankValue(rowB.original.bestRank || RoleplayRank.Unranked)
        );
      },
      cell: ({ row }: { row: Row<Participant> }) => {
        const bestRank = row.original.bestRank || RoleplayRank.Unranked;
        const style = roleplayRankStyle[bestRank];
        return (
          <Flex alignItems={"center"}>
            <RankIcon rank={bestRank} size={24} />
            <Box
              fontSize={14}
              fontWeight={500}
              ml="5px"
              color={style.textColor}
            >
              {style.rankText}
            </Box>
          </Flex>
        );
      },
    },
  ];

  const table = useReactTable({
    data: participants,
    columns,
    state: {
      sorting,
    },
    enableSorting: true,
    enableHiding: false,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row?.id || "",
  });

  return (
    <Box mt={36}>
      <DataTable
        table={table}
        className="overflow-visible"
        totalRecords={participants.length}
        pagination={false}
        emptyState={
          <Flex
            css={{
              background: Colors.WHITE,
              borderBottom: `1px solid ${Colors.LIGHT_GRAY2}`,
              minHeight: 200,
            }}
            width="100%"
            py="24px"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
          >
            <img
              src={NoParticipantsSvg}
              width={60}
              height={60}
              alt="No Participants"
            />
            <Box my={2}>No Participants Yet</Box>
          </Flex>
        }
      />
    </Box>
  );
}
